import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MonitoringLayout from "../components/MonitoringLayout";
import apiFetch from "../utils/apiFetch";

const STATUS_LABELS = {
  MASTER_REVIEW: "Мастер",
  LINE_CHIEF_REVIEW: "Тармоқ бошлиғи",
  BRANCH_ENGINEER_REVIEW: "Бош муҳандис",
};

const STATUS_BADGE = {
  MASTER_REVIEW: "master",
  LINE_CHIEF_REVIEW: "chief",
  BRANCH_ENGINEER_REVIEW: "engineer",
};

// ─── helpers ──────────────────────────────────────────────────────────────────
function toDayKey(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getUrgency(dueAt) {
  if (!dueAt) return "none";
  const diff = new Date(dueAt) - Date.now();
  if (diff < 0) return "overdue";
  if (diff < 86_400_000) return "today";
  if (diff < 3 * 86_400_000) return "soon";
  return "ok";
}

const URGENCY_RANK = { overdue: 4, today: 3, soon: 2, ok: 1, none: 0 };

const URGENCY_COLOR = {
  overdue: "#f87171",
  today: "#fb923c",
  soon: "#fbbf24",
  ok: "#60a5fa",
  none: "#6b7280",
};

function timeLeftLabel(dueAt) {
  if (!dueAt) return "—";
  const diff = new Date(dueAt) - Date.now();
  const h = Math.abs(diff) / 3_600_000;
  const d = Math.floor(h / 24);
  if (diff < 0) {
    if (d > 0) return `${d} kun o'tib ketdi`;
    return `${Math.floor(h)} soat o'tib ketdi`;
  }
  if (h < 1) return `${Math.round(h * 60)} daqiqa qoldi`;
  if (d === 0) return `${Math.floor(h)} soat qoldi`;
  return `${d} kun qoldi`;
}

// ─── MiniCalendar ─────────────────────────────────────────────────────────────
function MiniCalendar({ items, selectedDay, onSelect }) {
  const [view, setView] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const { year, month } = view;

  // worst urgency per day
  const dayMap = {};
  for (const inc of items) {
    const k = toDayKey(inc.due_at);
    if (!k) continue;
    const u = getUrgency(inc.due_at);
    if ((URGENCY_RANK[u] || 0) > (URGENCY_RANK[dayMap[k]] || 0)) dayMap[k] = u;
  }

  // count per day
  const dayCount = {};
  for (const inc of items) {
    const k = toDayKey(inc.due_at);
    if (k) dayCount[k] = (dayCount[k] || 0) + 1;
  }

  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = toDayKey(new Date().toISOString());

  const prevM = () =>
    setView((v) => {
      const d = new Date(v.year, v.month - 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  const nextM = () =>
    setView((v) => {
      const d = new Date(v.year, v.month + 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

  const monthLabel = new Date(year, month).toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric",
  });

  const cells = Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="cdash-cal">
      <div className="cdash-cal__nav">
        <button type="button" onClick={prevM} className="cdash-cal__arrow">‹</button>
        <span className="cdash-cal__month-label">{monthLabel}</span>
        <button type="button" onClick={nextM} className="cdash-cal__arrow">›</button>
      </div>

      <div className="cdash-cal__grid">
        {["Дш", "Сш", "Чш", "Пш", "Жм", "Шб", "Яш"].map((d) => (
          <div key={d} className="cdash-cal__dow">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`x${i}`} />;
          const k = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const urg = dayMap[k];
          const cnt = dayCount[k] || 0;
          const isToday = k === todayKey;
          const isSel = k === selectedDay;

          return (
            <button
              key={k}
              type="button"
              onClick={() => onSelect(isSel ? null : k)}
              className={[
                "cdash-cal__day",
                isToday && "is-today",
                isSel && "is-selected",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="cdash-cal__day-num">{day}</span>
              {urg && (
                <span
                  className="cdash-cal__dot"
                  style={{ background: URGENCY_COLOR[urg] }}
                >
                  {cnt > 1 ? cnt : ""}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="cdash-cal__legend">
        {[
          ["overdue", "Ўтган"],
          ["today", "Бугун"],
          ["soon", "2–3 кун"],
          ["ok", "Кейин"],
        ].map(([u, l]) => (
          <span key={u} className="cdash-cal__legend-item">
            <span
              className="cdash-cal__legend-dot"
              style={{ background: URGENCY_COLOR[u] }}
            />
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Incident row (read-only) ────────────────────────────────────────────────
function IncRow({ inc }) {
  const navigate = useNavigate();
  const urg = getUrgency(inc.due_at);
  const color = URGENCY_COLOR[urg];

  return (
    <div className="cdash-incrow">
      <div className="cdash-incrow__body">
        <div className="cdash-incrow__stripe" style={{ background: color }} />
        <div className="cdash-incrow__info">
          <div className="cdash-incrow__pole">{inc.pole?.name || "—"}</div>
          <div className="cdash-incrow__sub">
            {inc.pole?.network?.name && <span>{inc.pole.network.name}</span>}
            {inc.branch?.name && (
              <>
                <span className="cdash-incrow__sep">·</span>
                <span>{inc.branch.name}</span>
              </>
            )}
            <span className="cdash-incrow__sep">·</span>
            <span style={{ color: "#8b9ab5" }}>#{inc.id}</span>
          </div>
        </div>

        <div className="cdash-incrow__right">
          {inc.no_keys?.length > 0 && (
            <span className="mon-table__no-count">⚠ {inc.no_keys.length}</span>
          )}
          <span className={`mon-badge mon-badge--${STATUS_BADGE[inc.status] || "master"}`}>
            {STATUS_LABELS[inc.status] || inc.status}
          </span>
          <span className="cdash-incrow__time" style={{ color }}>
            {timeLeftLabel(inc.due_at)}
          </span>
          <button
            type="button"
            className="cdash-incrow__open-btn"
            onClick={() => navigate(`/monitoring/${inc.id}`)}
            title="Тўлиқ кўриш"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CentralDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [filter, setFilter] = useState("all"); // all | overdue

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/incidents/central-view");
      if (!res.ok) throw new Error("Юкланмади");
      setData(await res.json());
    } catch (e) {
      if (e?.status === 401) navigate("/login");
      else if (e?.status === 403) navigate("/no-access");
      else setError(e.message || "Хатолик");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const stats = data?.stats || {};

  // Sort by due_at ASC
  const sorted = items.slice().sort((a, b) => {
    if (!a.due_at && !b.due_at) return 0;
    if (!a.due_at) return 1;
    if (!b.due_at) return -1;
    return new Date(a.due_at) - new Date(b.due_at);
  });

  // Apply filter
  const now = Date.now();
  let listItems = sorted;
  if (selectedDay) {
    listItems = sorted.filter((i) => toDayKey(i.due_at) === selectedDay);
  } else if (filter === "overdue") {
    listItems = sorted.filter((i) => i.due_at && new Date(i.due_at) < now);
  }

  const calItems = items.filter((i) => i.due_at);

  return (
    <MonitoringLayout>
      <div className="mon-page-header">
        <h1 className="mon-page-title">Марказий бошқарув панели</h1>
        <button
          type="button"
          className="mon-pagination__btn"
          onClick={load}
          disabled={loading}
        >
          ↺ Янгилаш
        </button>
      </div>

      {/* ── Stats ── */}
      {data && (
        <div className="cdash-stats">
          <div
            className="cdash-stat"
            style={{ borderTopColor: "#f87171" }}
            onClick={() => { setFilter("overdue"); setSelectedDay(null); }}
            role="button"
          >
            <div className="cdash-stat__val" style={{ color: stats.overdue > 0 ? "#f87171" : undefined }}>
              {stats.overdue || 0}
            </div>
            <div className="cdash-stat__label">Муддати ўтган</div>
          </div>
          <div
            className="cdash-stat"
            style={{ borderTopColor: "#5b7ef5" }}
            onClick={() => { setFilter("all"); setSelectedDay(null); }}
            role="button"
          >
            <div className="cdash-stat__val">{stats.total_open || 0}</div>
            <div className="cdash-stat__label">Жами фаол нуқсонлар</div>
          </div>
        </div>
      )}

      {loading && <div className="mon-loading">Юкланмоқда...</div>}
      {error && !loading && <div className="mon-error">{error}</div>}

      {!loading && !error && data && (
        <div className="cdash-layout">
          {/* ── Left: calendar ── */}
          <div className="cdash-col-left">
            <div className="mon-card">
              <p className="mon-card__title">Муддат календари</p>
              <MiniCalendar
                items={calItems}
                selectedDay={selectedDay}
                onSelect={(k) => {
                  setSelectedDay(k);
                  if (k) setFilter("day");
                  else setFilter("all");
                }}
              />
            </div>

            {selectedDay && (
              <div className="mon-card" style={{ marginTop: 12 }}>
                <p className="mon-card__title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>
                    {new Date(selectedDay + "T00:00:00").toLocaleDateString("ru-RU", {
                      day: "numeric",
                      month: "long",
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedDay(null); setFilter("all"); }}
                    style={{ background: "none", border: "none", color: "#8b9ab5", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
                  >
                    ✕
                  </button>
                </p>
                {listItems.length === 0 ? (
                  <div style={{ color: "#8b9ab5", fontSize: 13, padding: "8px 0" }}>
                    Бу кунда аниқланган нуқсонлар йўқ
                  </div>
                ) : (
                  <div className="cdash-day-list">
                    {listItems.map((inc) => (
                      <div
                        key={inc.id}
                        className="cdash-day-item"
                        onClick={() => navigate(`/monitoring/${inc.id}`)}
                      >
                        <span
                          className="cdash-day-item__dot"
                          style={{ background: URGENCY_COLOR[getUrgency(inc.due_at)] }}
                        />
                        <span className="cdash-day-item__name">{inc.pole?.name || "—"}</span>
                        {inc.pole?.network?.name && (
                          <span className="cdash-day-item__net">{inc.pole.network.name}</span>
                        )}
                        <span className="cdash-day-item__id">#{inc.id}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Right: incident list ── */}
          <div className="cdash-col-right">
            <div className="mon-card cdash-list-card">
              <div className="cdash-list-header">
                <p className="mon-card__title" style={{ margin: 0 }}>
                  {selectedDay
                    ? new Date(selectedDay + "T00:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "long" })
                    : filter === "overdue"
                    ? "Муддати ўтган"
                    : "Барча фаол нуқсонлар"}
                </p>
                {!selectedDay && (
                  <div className="cdash-filter-tabs">
                    {[
                      { v: "all", label: "Барчаси" },
                      {
                        v: "overdue",
                        label: stats.overdue
                          ? `Ўтган (${stats.overdue})`
                          : "Ўтган",
                      },
                    ].map(({ v, label }) => (
                      <button
                        key={v}
                        type="button"
                        className={"mon-status-tab" + (filter === v ? " is-active" : "")}
                        onClick={() => setFilter(v)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="cdash-list">
                {listItems.length === 0 && (
                  <div className="mon-empty" style={{ padding: "32px 0" }}>
                    Аниқланган нуқсонлар топилмади
                  </div>
                )}
                {listItems.map((inc) => (
                  <IncRow key={inc.id} inc={inc} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </MonitoringLayout>
  );
}
